/**
 * Executive Demo Guide Page
 * Showcases the HOS platform features tailored for different executive roles
 */

import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { useNavigate } from "react-router-dom";
import {
  Briefcase, DollarSign, Stethoscope, TrendingUp, 
  Command, Brain, Shield, Users, Heart, FileText,
  Printer, ChevronRight, Sparkles, Play, ExternalLink,
  Target, CheckCircle2, AlertTriangle, Clock
} from "lucide-react";

const demoFeatures = {
  ceo: [
    { 
      name: "Command Center Dashboard", 
      path: "/hos", 
      icon: Command,
      description: "Unified view of all patient statuses at a glance",
      keyMetric: "Average PPS: 65%",
      highlight: true
    },
    { 
      name: "AI Predictive Alerts", 
      path: "/predictive-alerts", 
      icon: Brain,
      description: "AI predicts patient decline with 84% accuracy",
      keyMetric: "48-72 hour predictions",
      highlight: true
    },
    { 
      name: "Command Palette", 
      path: "/hos", 
      icon: Command,
      description: "Every workflow accessible in 2 keystrokes (Ctrl+K)",
      keyMetric: "Voice-first, keyboard-first"
    },
    { 
      name: "Staff Portal Quick Actions", 
      path: "/staff-portal", 
      icon: Users,
      description: "Staff see exactly what needs attention first"
    }
  ],
  cfo: [
    { 
      name: "CMS Compliance Dashboard", 
      path: "/hos", 
      icon: Shield,
      description: "Real-time compliance grade for every visit note",
      keyMetric: "Hard-stop enforcement",
      highlight: true
    },
    { 
      name: "LCD Eligibility Checker", 
      path: "/lcd-eligibility", 
      icon: CheckCircle2,
      description: "Catches eligibility issues before certification",
      keyMetric: "Prevents recoupment",
      highlight: true
    },
    { 
      name: "5% Volunteer Rule Report", 
      path: "/volunteer-bereavement", 
      icon: Users,
      description: "Automated CMS 5% volunteer requirement tracking"
    },
    { 
      name: "Physician CTI Workflow", 
      path: "/physician-cti", 
      icon: FileText,
      description: "Faster physician certification = faster billing"
    },
    { 
      name: "Scheduled Reports", 
      path: "/admin-settings", 
      icon: Clock,
      description: "Automated daily census, weekly compliance reports"
    },
    { 
      name: "Print Patient Summary", 
      path: "/hos", 
      icon: Printer,
      description: "Complete chart available in 30 seconds",
      keyMetric: "Audit readiness"
    }
  ],
  hmd: [
    { 
      name: "Patient Acuity Trends", 
      path: "/acuity-trends", 
      icon: TrendingUp,
      description: "See declining vs improving patients instantly",
      highlight: true
    },
    { 
      name: "Predictive Escalation Alerts", 
      path: "/predictive-alerts", 
      icon: Brain,
      description: "AI model: XGBoost + LSTM, 12 months training data",
      keyMetric: "84% accuracy",
      highlight: true
    },
    { 
      name: "RN Command Center", 
      path: "/rn-command-center", 
      icon: Stethoscope,
      description: "Voice documentation, 40% faster with smart templates"
    },
    { 
      name: "HOPE Assessment Compliance", 
      path: "/rn-command-center", 
      icon: Heart,
      description: "HOPE timeline enforcement (Day 6-15, Day 16-30)"
    },
    { 
      name: "Physician CTI Attestation", 
      path: "/physician-cti", 
      icon: FileText,
      description: "Complete audit trail of clinical determinations"
    },
    { 
      name: "AI Care Plan Recommendations", 
      path: "/ai-care-plans", 
      icon: Sparkles,
      description: "GPT-5.2 powered intervention suggestions"
    }
  ],
  bd: [
    { 
      name: "Family Portal", 
      path: "/family-portal", 
      icon: Heart,
      description: "Families see who's coming, when, and what happened",
      keyMetric: "24/7 transparency",
      highlight: true
    },
    { 
      name: "Family Communication", 
      path: "/family-messaging", 
      icon: Users,
      description: "Two-way secure messaging with care team"
    },
    { 
      name: "LCD Eligibility Checker", 
      path: "/lcd-eligibility", 
      icon: CheckCircle2,
      description: "Instant eligibility evaluation during referral calls",
      highlight: true
    },
    { 
      name: "Print & Share Summary", 
      path: "/hos", 
      icon: Printer,
      description: "Share comprehensive summaries with discharge planners"
    },
    { 
      name: "Predictive Analytics", 
      path: "/predictive-alerts", 
      icon: Brain,
      description: "Marketing: 'We don't just react—we predict and prevent'"
    }
  ]
};

const roleConfig = {
  ceo: { 
    title: "CEO", 
    subtitle: "Strategic Vision & Competitive Advantage",
    icon: Briefcase, 
    color: "text-blue-400",
    bgColor: "bg-blue-500/20",
    borderColor: "border-blue-500/50",
    hook: "This platform transforms hospice operations from reactive paper-based workflows to a proactive, AI-powered command center—reducing compliance risk while improving care quality.",
    closing: "This positions Goodwin Cares as the technology leader in hospice—enabling growth without proportional staffing increases."
  },
  cfo: { 
    title: "CFO", 
    subtitle: "Revenue Protection, Cost Reduction & Compliance",
    icon: DollarSign, 
    color: "text-green-400",
    bgColor: "bg-green-500/20",
    borderColor: "border-green-500/50",
    hook: "This system directly impacts the bottom line: reducing claim denials through CMS compliance automation while cutting documentation time by 40%.",
    closing: "Every feature is designed to protect revenue and demonstrate CMS compliance—reducing audit risk while accelerating cash flow."
  },
  hmd: { 
    title: "HMD", 
    subtitle: "Clinical Quality, Physician Workflows & Care Outcomes",
    icon: Stethoscope, 
    color: "text-teal-400",
    bgColor: "bg-teal-500/20",
    borderColor: "border-teal-500/50",
    hook: "This platform puts clinical decision support directly into your workflow—surfacing patients who need attention before crises occur.",
    closing: "This platform elevates clinical oversight—ensuring every patient receives evidence-based, CMS-compliant care while reducing physician administrative burden."
  },
  bd: { 
    title: "Business Development", 
    subtitle: "Referral Growth, Differentiation & Family Satisfaction",
    icon: TrendingUp, 
    color: "text-amber-400",
    bgColor: "bg-amber-500/20",
    borderColor: "border-amber-500/50",
    hook: "This platform creates competitive differentiation: we can show referral sources real-time transparency and give families unprecedented visibility into their loved one's care.",
    closing: "This technology story wins referrals: we're not just a hospice, we're a technology-forward care partner with unprecedented family transparency."
  }
};

export default function ExecutiveDemoGuide() {
  const navigate = useNavigate();
  const [activeRole, setActiveRole] = useState("ceo");

  const role = roleConfig[activeRole];
  const features = demoFeatures[activeRole];
  const RoleIcon = role.icon;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <Badge className="mb-4 bg-teal-500/20 text-teal-300 border-0">
            <Sparkles className="w-3 h-3 mr-1" />
            Executive Demo Guide
          </Badge>
          <h1 className="text-3xl font-bold text-white mb-2">
            Hospice Operating System
          </h1>
          <p className="text-slate-400">
            Tailored demonstrations for key stakeholders
          </p>
        </div>

        {/* Role Tabs */}
        <Tabs value={activeRole} onValueChange={setActiveRole} className="mb-8">
          <TabsList className="grid grid-cols-4 gap-2 bg-slate-800/50 p-1 h-auto">
            {Object.entries(roleConfig).map(([key, config]) => {
              const Icon = config.icon;
              return (
                <TabsTrigger 
                  key={key} 
                  value={key}
                  className={`flex items-center gap-2 py-3 data-[state=active]:${config.bgColor} data-[state=active]:${config.color}`}
                >
                  <Icon className="w-4 h-4" />
                  <span className="hidden sm:inline">{config.title}</span>
                </TabsTrigger>
              );
            })}
          </TabsList>
        </Tabs>

        {/* Role Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left: Role Info */}
          <Card className={`bg-slate-800/50 border-slate-700 ${role.borderColor}`}>
            <CardHeader>
              <div className={`p-3 rounded-lg ${role.bgColor} w-fit mb-3`}>
                <RoleIcon className={`w-8 h-8 ${role.color}`} />
              </div>
              <CardTitle className="text-white text-xl">{role.title} Demo</CardTitle>
              <CardDescription className="text-slate-400">
                {role.subtitle}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h4 className="text-sm font-medium text-teal-400 mb-2">Opening Hook</h4>
                <p className="text-sm text-slate-300 italic">"{role.hook}"</p>
              </div>
              <Separator className="bg-slate-700" />
              <div>
                <h4 className="text-sm font-medium text-teal-400 mb-2">Closing Statement</h4>
                <p className="text-sm text-slate-300 italic">"{role.closing}"</p>
              </div>
              <Separator className="bg-slate-700" />
              <div>
                <h4 className="text-sm font-medium text-slate-400 mb-2">Demo Duration</h4>
                <Badge variant="outline" className="border-slate-600 text-slate-300">
                  <Clock className="w-3 h-3 mr-1" />
                  {activeRole === 'hmd' ? '7-10 minutes' : '5-7 minutes'}
                </Badge>
              </div>
            </CardContent>
          </Card>

          {/* Right: Features */}
          <div className="lg:col-span-2">
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Target className="w-5 h-5 text-teal-400" />
                  Demo Path ({features.length} Features)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[400px] pr-4">
                  <div className="space-y-3">
                    {features.map((feature, idx) => {
                      const FeatureIcon = feature.icon;
                      return (
                        <div 
                          key={idx}
                          className={`p-4 rounded-lg border transition-colors cursor-pointer hover:bg-slate-700/50 ${
                            feature.highlight 
                              ? 'bg-slate-700/30 border-teal-500/50' 
                              : 'bg-slate-800/30 border-slate-700'
                          }`}
                          onClick={() => navigate(feature.path)}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex items-start gap-3">
                              <div className={`p-2 rounded-lg ${feature.highlight ? 'bg-teal-500/20' : 'bg-slate-700'}`}>
                                <FeatureIcon className={`w-4 h-4 ${feature.highlight ? 'text-teal-400' : 'text-slate-400'}`} />
                              </div>
                              <div>
                                <div className="flex items-center gap-2">
                                  <h4 className="font-medium text-white">{feature.name}</h4>
                                  {feature.highlight && (
                                    <Badge className="bg-teal-500/20 text-teal-300 text-xs border-0">
                                      Key Feature
                                    </Badge>
                                  )}
                                </div>
                                <p className="text-sm text-slate-400 mt-1">{feature.description}</p>
                                {feature.keyMetric && (
                                  <Badge variant="outline" className="mt-2 border-slate-600 text-slate-300 text-xs">
                                    {feature.keyMetric}
                                  </Badge>
                                )}
                              </div>
                            </div>
                            <Button variant="ghost" size="icon" className="text-slate-400 hover:text-white">
                              <ChevronRight className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Quick Actions */}
        <Card className="mt-6 bg-slate-800/50 border-slate-700">
          <CardContent className="py-4">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-2 text-slate-400">
                <AlertTriangle className="w-4 h-4 text-amber-400" />
                <span className="text-sm">MOCKED: E-Prescribing, SMS, Email (awaiting API keys)</span>
              </div>
              <div className="flex items-center gap-3">
                <Button 
                  variant="outline" 
                  className="border-slate-600 text-slate-300"
                  onClick={() => navigate('/hos')}
                >
                  <Play className="w-4 h-4 mr-2" />
                  Start Demo
                </Button>
                <Button 
                  className="bg-teal-600 hover:bg-teal-700 text-white"
                  onClick={() => window.open('/login', '_blank')}
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Open in New Tab
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Credentials */}
        <div className="mt-6 text-center text-sm text-slate-500">
          <p>Demo Credentials: admin_test@goodwincares.com / Test1234!</p>
        </div>
      </div>
    </div>
  );
}
